import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Modal from 'react-bootstrap/Modal';
import { fetchCsrfToken, submitRightOfEntry } from '../../api/rightOfEntry';
import './RightOfEntry.css';

const PROPERTY_TYPES = [
  { value: 'residential', label: 'Residential' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'public_land', label: 'Public Land' },
  { value: 'agricultural', label: 'Agricultural' },
  { value: 'recreational', label: 'Recreational' },
  { value: 'other', label: 'Other' },
];

const CONTACT_METHODS = [
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'either', label: 'Either' },
];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

const initialFormData = {
  // Property Information
  property_address: '',
  property_city: '',
  property_state: '',
  property_zip_code: '',
  property_county: '',
  parcel_number: '',
  property_type: 'residential',
  property_acreage: '',
  is_hoa: false,
  hoa_name: '',
  // Owner Information
  owner_first_name: '',
  owner_last_name: '',
  owner_email: '',
  owner_phone: '',
  owner_mailing_address: '',
  // Purpose and Scope
  scope_accepted: false,
  // Access Instructions
  access_instructions: '',
  preferred_contact_method: 'either',
  preferred_contact_time: '',
  // Certification
  certify_authorized: false,
  terms_accepted: false,
  electronic_signature: '',
  signature_date: new Date().toISOString().split('T')[0],
};

// Human-readable labels for the missing-fields summary, with section hints.
const FIELD_LABELS = {
  property_address: 'Property Address',
  property_city: 'City',
  property_state: 'State',
  property_zip_code: 'Zip Code',
  owner_first_name: "Property Owner's First Name",
  owner_last_name: "Property Owner's Last Name",
  owner_email: "Property Owner's Email",
  owner_phone: "Property Owner's Phone Number",
  scope_accepted: 'Acknowledge the authorized activities (Section 3)',
  terms_accepted: 'Accept the Terms & Conditions (Section 4)',
  certify_authorized: 'Certify your authority to grant entry (Section 6)',
  electronic_signature: 'Electronic Signature (Section 6)',
  signature_date: 'Signature Date (Section 6)',
};

function RightOfEntry() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [submitAttemptCount, setSubmitAttemptCount] = useState(0);
  const errorSummaryRef = useRef(null);

  useEffect(() => {
    fetchCsrfToken().catch(console.error);
  }, []);

  useEffect(() => {
    if (!showSuccessModal) return;

    setCountdown(5);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          navigate('/');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [showSuccessModal, navigate]);

  // After a failed submit, bring the missing-fields summary into view.
  useEffect(() => {
    if (submitAttemptCount > 0 && errorSummaryRef.current) {
      errorSummaryRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [submitAttemptCount]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // Scroll to and focus a field when its summary link is clicked.
  const focusField = (fieldName) => {
    const el = document.querySelector(`[name="${fieldName}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus({ preventScroll: true });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Property Information
    if (!formData.property_address.trim()) {
      newErrors.property_address = 'Property address is required';
    }
    if (!formData.property_city.trim()) {
      newErrors.property_city = 'City is required';
    }
    if (!formData.property_state) {
      newErrors.property_state = 'State is required';
    }
    if (!formData.property_zip_code.trim()) {
      newErrors.property_zip_code = 'Zip code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.property_zip_code.trim())) {
      newErrors.property_zip_code = 'Invalid zip code (use 12345 or 12345-6789)';
    }

    // Owner Information
    if (!formData.owner_first_name.trim()) {
      newErrors.owner_first_name = 'First name is required';
    }
    if (!formData.owner_last_name.trim()) {
      newErrors.owner_last_name = 'Last name is required';
    }
    if (!formData.owner_email.trim()) {
      newErrors.owner_email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.owner_email)) {
      newErrors.owner_email = 'Invalid email format';
    }
    if (!formData.owner_phone.trim()) {
      newErrors.owner_phone = 'Phone number is required';
    }

    // Purpose and Scope
    if (!formData.scope_accepted) {
      newErrors.scope_accepted = 'You must acknowledge and agree to the authorized activities';
    }

    // Terms & Conditions
    if (!formData.terms_accepted) {
      newErrors.terms_accepted = 'You must accept the terms and conditions';
    }

    // Certification
    if (!formData.certify_authorized) {
      newErrors.certify_authorized = 'You must certify that you are the property owner or authorized representative';
    }
    if (!formData.electronic_signature.trim()) {
      newErrors.electronic_signature = 'Electronic signature is required';
    } else if (formData.electronic_signature.trim().length < 2) {
      newErrors.electronic_signature = 'Please type your full legal name';
    }
    if (!formData.signature_date) {
      newErrors.signature_date = 'Signature date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setSubmitAttemptCount((count) => count + 1);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await submitRightOfEntry(formData);
      setShowSuccessModal(true);
    } catch (error) {
      setSubmitError(error.message || 'Failed to submit form. Please try again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const errorKeys = Object.keys(errors).filter((key) => errors[key]);

  return (
    <>
      <Modal show={showSuccessModal} centered backdrop="static" keyboard={false}>
        <Modal.Body className="text-center py-5">
          <div className="text-primary mb-3">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
          </div>
          <h3 className="mb-3">Form Submitted Successfully!</h3>
          <p className="text-muted mb-2">
            Your Right of Entry form has been submitted. Our team will review the details and contact you to confirm access arrangements.
          </p>
          <p className="text-muted small">
            Redirecting to home in {countdown} second{countdown !== 1 ? 's' : ''}...
          </p>
        </Modal.Body>
      </Modal>

      <div className="roe-page">
        <Helmet>
          <title>Property Evaluation | Mountain Pine Beetle Foundation</title>
          <meta name="description" content="Authorize a Mountain Pine Beetle Foundation property evaluation — grant right of entry for tree inspection, preventive treatment, monitoring, or infested tree removal on your property." />
          <meta name="keywords" content="property evaluation, right of entry, property access, tree inspection, beetle treatment, forest conservation, mountain pine beetle, property owner authorization" />
          <link rel="canonical" href="https://www.mountainpinebeetle.org/right-of-entry" />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://www.mountainpinebeetle.org/right-of-entry" />
          <meta property="og:title" content="Property Evaluation | Mountain Pine Beetle Foundation" />
          <meta property="og:description" content="Authorize a property evaluation for tree inspection, treatment, or monitoring on your property." />
          <meta property="og:site_name" content="Mountain Pine Beetle Foundation" />
          <meta property="og:locale" content="en_US" />
          <meta property="og:image" content="https://www.mountainpinebeetle.org/mpb_logo.png" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Property Evaluation | Mountain Pine Beetle Foundation" />
          <meta name="twitter:description" content="Authorize a property evaluation for tree inspection, treatment, or monitoring on your property." />
          <meta name="twitter:image" content="https://www.mountainpinebeetle.org/mpb_logo.png" />
          <script type="application/ld+json">{`
            {
              "@context": "https://schema.org",
              "@type": "WebPage",
              "name": "Property Evaluation",
              "description": "Authorize a property evaluation for tree inspection, treatment, or monitoring on your property.",
              "url": "https://www.mountainpinebeetle.org/right-of-entry",
              "isPartOf": {
                "@type": "WebSite",
                "name": "Mountain Pine Beetle Foundation",
                "url": "https://www.mountainpinebeetle.org"
              }
            }
          `}</script>
        </Helmet>

        <section className="roe-header py-5">
          <Container>
            <div className="text-center py-3">
              <h1 className="mb-3">Property Evaluation</h1>
              <p className="mx-auto mb-0" style={{ maxWidth: '580px' }}>
                Authorize Mountain Pine Beetle Foundation personnel to evaluate your property — inspection, treatment assessment, and monitoring for beetle activity.
              </p>
            </div>
          </Container>
        </section>

        <Container className="py-4">
          <Row className="justify-content-center">
            <Col lg={10}>

              {errorKeys.length > 0 && (
                <Alert variant="danger" className="mb-4" ref={errorSummaryRef}>
                  <div className="d-flex align-items-center fw-bold mb-2">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="me-2 flex-shrink-0" aria-hidden="true">
                      <path d="M12 2L1 21h22L12 2zm0 4l7.53 13H4.47L12 6zm-1 5v4h2v-4h-2zm0 6v2h2v-2h-2z" />
                    </svg>
                    <span>
                      Your Property Evaluation form isn't ready to submit yet — please complete the{' '}
                      {errorKeys.length} {errorKeys.length === 1 ? 'item' : 'items'} below:
                    </span>
                  </div>
                  <ul className="mb-0 ps-4">
                    {errorKeys.map((key) => (
                      <li key={key}>
                        <Alert.Link
                          as="button"
                          type="button"
                          className="p-0 border-0 bg-transparent align-baseline text-decoration-underline"
                          onClick={() => focusField(key)}
                        >
                          {FIELD_LABELS[key] || key}
                        </Alert.Link>
                        {' — '}
                        {errors[key]}
                      </li>
                    ))}
                  </ul>
                </Alert>
              )}

              {submitError && (
                <Alert variant="danger" dismissible onClose={() => setSubmitError(null)} className="mb-4">
                  {submitError}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>

                {/* Section 1: Property Information */}
                <Card className="roe-form-card mb-4">
                  <Card.Body>
                    <h5 className="roe-section-title">
                      <span className="roe-section-number">1</span>
                      Property Information
                    </h5>
                    <p className="roe-section-desc">Provide details about the property where access is being granted.</p>

                    <Form.Group className="mb-3">
                      <Form.Label>Property Address <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="property_address"
                        value={formData.property_address}
                        onChange={handleChange}
                        isInvalid={!!errors.property_address}
                        placeholder="Street address"
                      />
                      <Form.Control.Feedback type="invalid">{errors.property_address}</Form.Control.Feedback>
                    </Form.Group>

                    <Row>
                      <Col md={5}>
                        <Form.Group className="mb-3">
                          <Form.Label>City <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            name="property_city"
                            value={formData.property_city}
                            onChange={handleChange}
                            isInvalid={!!errors.property_city}
                            placeholder="City"
                          />
                          <Form.Control.Feedback type="invalid">{errors.property_city}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>State <span className="text-danger">*</span></Form.Label>
                          <Form.Select
                            name="property_state"
                            value={formData.property_state}
                            onChange={handleChange}
                            isInvalid={!!errors.property_state}
                          >
                            <option value="">Select</option>
                            {US_STATES.map((st) => (
                              <option key={st} value={st}>{st}</option>
                            ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">{errors.property_state}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Zip Code <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            name="property_zip_code"
                            value={formData.property_zip_code}
                            onChange={handleChange}
                            isInvalid={!!errors.property_zip_code}
                            placeholder="12345"
                          />
                          <Form.Control.Feedback type="invalid">{errors.property_zip_code}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>County</Form.Label>
                          <Form.Control
                            type="text"
                            name="property_county"
                            value={formData.property_county}
                            onChange={handleChange}
                            placeholder="County (optional)"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Parcel Number</Form.Label>
                          <Form.Control
                            type="text"
                            name="parcel_number"
                            value={formData.parcel_number}
                            onChange={handleChange}
                            placeholder="Parcel / APN (optional)"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Approx. Acreage</Form.Label>
                          <Form.Control
                            type="text"
                            name="property_acreage"
                            value={formData.property_acreage}
                            onChange={handleChange}
                            placeholder="e.g. 2.5 acres"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-0">
                      <Form.Label>Property Type</Form.Label>
                      <Form.Select
                        name="property_type"
                        value={formData.property_type}
                        onChange={handleChange}
                      >
                        {PROPERTY_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mt-3 mb-0">
                      <Form.Check
                        type="checkbox"
                        id="is_hoa"
                        name="is_hoa"
                        checked={formData.is_hoa}
                        onChange={handleChange}
                        label="This property is part of a Homeowners Association (HOA)"
                      />
                    </Form.Group>

                    {formData.is_hoa && (
                      <Form.Group className="mt-3 mb-0">
                        <Form.Label>HOA Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="hoa_name"
                          value={formData.hoa_name}
                          onChange={handleChange}
                          placeholder="Enter HOA name"
                        />
                      </Form.Group>
                    )}
                  </Card.Body>
                </Card>

                {/* Section 2: Granting Right of Entry */}
                <Card className="roe-form-card mb-4">
                  <Card.Body>
                    <h5 className="roe-section-title">
                      <span className="roe-section-number">2</span>
                      Granting Right of Entry
                    </h5>
                    <p className="roe-section-desc">Information about the property owner or authorized representative granting access.</p>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            name="owner_first_name"
                            value={formData.owner_first_name}
                            onChange={handleChange}
                            isInvalid={!!errors.owner_first_name}
                            placeholder="First name"
                          />
                          <Form.Control.Feedback type="invalid">{errors.owner_first_name}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Last Name <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            name="owner_last_name"
                            value={formData.owner_last_name}
                            onChange={handleChange}
                            isInvalid={!!errors.owner_last_name}
                            placeholder="Last name"
                          />
                          <Form.Control.Feedback type="invalid">{errors.owner_last_name}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="email"
                            name="owner_email"
                            value={formData.owner_email}
                            onChange={handleChange}
                            isInvalid={!!errors.owner_email}
                            placeholder="Email address"
                          />
                          <Form.Control.Feedback type="invalid">{errors.owner_email}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Phone <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="tel"
                            name="owner_phone"
                            value={formData.owner_phone}
                            onChange={handleChange}
                            isInvalid={!!errors.owner_phone}
                            placeholder="Phone number"
                          />
                          <Form.Control.Feedback type="invalid">{errors.owner_phone}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-0">
                      <Form.Label>Mailing Address (if different from property)</Form.Label>
                      <Form.Control
                        type="text"
                        name="owner_mailing_address"
                        value={formData.owner_mailing_address}
                        onChange={handleChange}
                        placeholder="Mailing address (optional)"
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>

                {/* Section 3: Purpose and Scope of Work */}
                <Card className="roe-form-card mb-4">
                  <Card.Body>
                    <h5 className="roe-section-title">
                      <span className="roe-section-number">3</span>
                      Purpose &amp; Scope of Work
                    </h5>
                    <p className="roe-section-desc">The following activities are authorized under this Right of Entry agreement.</p>

                    <div className="roe-scope-box mb-3">
                      <p className="mb-2"><strong>Activities Authorized by This Agreement:</strong></p>
                      <ol className="roe-scope-list">
                        <li><strong>Visual Inspection</strong> &mdash; Visual inspection of trees on the property for signs of mountain pine beetle (MPB) infestation and damage, including but not limited to pitch tubes, boring dust, needle discoloration, and bark condition assessment.</li>
                        <li><strong>Data Collection &amp; Mapping</strong> &mdash; Collection of field data and GPS mapping of infested, damaged, or high-risk trees to support monitoring, reporting, and conservation planning efforts.</li>
                        <li><strong>Tree Marking</strong> &mdash; Marking of specific trees with temporary survey ribbon or non-toxic paint for identification, assessment, or treatment planning purposes. All markings will be temporary and non-damaging.</li>
                        <li><strong>Tree Removal &amp; Chemical Treatment</strong> &mdash; Tree removal, chemical treatment (e.g., preventive insecticide application), or pheromone deployment are <strong>not</strong> authorized under this Right of Entry agreement. Any such activities will require a separate, subsequent written agreement between the property owner and the Foundation.</li>
                      </ol>
                    </div>

                    <Form.Group className="mb-0">
                      <Form.Check
                        type="checkbox"
                        id="scope_accepted"
                        name="scope_accepted"
                        checked={formData.scope_accepted}
                        onChange={handleChange}
                        isInvalid={!!errors.scope_accepted}
                        label="I acknowledge and agree to the authorized activities described above."
                        feedback={errors.scope_accepted}
                        feedbackType="invalid"
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>

                {/* Section 4: Terms and Conditions */}
                <Card className="roe-form-card mb-4">
                  <Card.Body>
                    <h5 className="roe-section-title">
                      <span className="roe-section-number">4</span>
                      Terms &amp; Conditions
                    </h5>

                    <div className="roe-terms-box mb-3">
                      <p><strong>By granting right of entry, you agree to the following:</strong></p>

                      <p className="roe-terms-subheading">Duration</p>
                      <ol start="1">
                        <li>This Right of Entry agreement is valid for a period of <strong>ninety (90) days</strong> from the date of signature. Upon expiration, a new Right of Entry agreement must be executed for continued access. The Foundation will not enter the property after the 90-day period unless a new agreement is in effect.</li>
                      </ol>

                      <p className="roe-terms-subheading">Access</p>
                      <ol start="2">
                        <li>All property access under this agreement shall be limited to <strong>daylight hours only</strong> (sunrise to sunset, local time). No entry shall occur before sunrise or after sunset.</li>
                        <li>The Foundation will attempt to provide at least <strong>24 hours advance notice</strong> before each entry via email or text message to the contact information provided by the property owner. In the event that notice cannot be delivered, the Foundation will make a reasonable effort to contact the property owner by an alternative method prior to entry.</li>
                      </ol>

                      <p className="roe-terms-subheading">General Terms</p>
                      <ol start="4">
                        <li>Mountain Pine Beetle Foundation (&ldquo;the Foundation&rdquo;) and its authorized agents, employees, and contractors are granted permission to enter the property described above for the authorized activities during the effective period of this agreement.</li>
                        <li>The Foundation will make reasonable efforts to minimize disruption to the property and will restore the property to its original condition to the extent practicable.</li>
                        <li>The property owner or authorized representative may revoke this right of entry at any time by providing written notice to the Foundation.</li>
                        <li>All data, photographs, and findings collected during the entry may be used by the Foundation for research, education, and conservation purposes. Tree observations (species, health status, and observation date) may appear on the Foundation&rsquo;s public Impact Map and research data feeds at approximate locations only (rounded to about 110 meters); your name, address, contact details, parcel number, and access instructions are never published. No other personally identifiable information will be published without consent. See our <Link to="/privacy">Privacy Policy</Link>.</li>
                        <li>This agreement does not constitute a transfer of any property rights or interests.</li>
                        <li>You acknowledge that information you submit through this form and any related correspondence (including names, contact details, property details, photographs, and free-text messages) may be transmitted to and processed by third-party service providers &mdash; including large language model (LLM) and other artificial intelligence services &mdash; for the purposes of intake, categorization, summarization, translation, drafting, and communications on behalf of the Foundation. The Foundation will use commercially reasonable efforts to select providers with appropriate data-handling, security, and privacy safeguards, and will not sell your personal information.</li>
                      </ol>

                      <p className="roe-terms-subheading">Liability</p>
                      <ol start="10">
                        <li>The Foundation maintains comprehensive general liability insurance covering its personnel, agents, and contractors for bodily injury and property damage arising from activities performed under this agreement. Proof of insurance will be provided upon request.</li>
                        <li>The Foundation shall not be held liable for any loss, damage, or injury arising from pre-existing conditions on the property, including but not limited to: hazardous terrain, unstable structures, wildlife, environmental contamination, or the natural progression of mountain pine beetle infestation and associated tree mortality.</li>
                        <li>The property owner agrees to <strong>hold harmless</strong> the Foundation, its officers, employees, agents, and contractors from any claims related to minor property disturbances that may naturally occur during the course of authorized inspection activities, including but not limited to: foot traffic on landscaped areas, minor soil disturbance, displacement of ground cover or leaf litter, disturbance of brush or low-hanging branches during tree access, temporary tire tracks from vehicle access on unpaved surfaces, and incidental contact with fencing, gates, or other property features during normal ingress and egress.</li>
                        <li>The property owner agrees to <strong>indemnify and hold harmless</strong> the Foundation, its officers, employees, agents, and contractors from any claims, damages, losses, or expenses (including reasonable attorney fees) arising from: (a) conditions on the property not disclosed by the property owner; (b) the property owner&rsquo;s own negligence; or (c) any misrepresentation made by the property owner in this agreement.</li>
                        <li>Neither party shall be liable to the other for any indirect, incidental, consequential, or punitive damages arising out of or related to this agreement, regardless of the cause of action or theory of liability.</li>
                      </ol>
                    </div>

                    <Form.Group className="mb-0">
                      <Form.Check
                        type="checkbox"
                        id="terms_accepted"
                        name="terms_accepted"
                        checked={formData.terms_accepted}
                        onChange={handleChange}
                        isInvalid={!!errors.terms_accepted}
                        label="I have read and agree to the terms and conditions above."
                        feedback={errors.terms_accepted}
                        feedbackType="invalid"
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>

                {/* Section 5: Special Instructions for Access */}
                <Card className="roe-form-card mb-4">
                  <Card.Body>
                    <h5 className="roe-section-title">
                      <span className="roe-section-number">5</span>
                      Special Instructions for Access
                    </h5>
                    <p className="roe-section-desc">Provide any special instructions our team should know before accessing the property.</p>

                    <Form.Group className="mb-3">
                      <Form.Label>Access Instructions</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="access_instructions"
                        value={formData.access_instructions}
                        onChange={handleChange}
                        placeholder="Gate codes, locked gates, pets on property, parking instructions, terrain hazards, seasonal road conditions, etc."
                      />
                    </Form.Group>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Preferred Contact Method</Form.Label>
                          <Form.Select
                            name="preferred_contact_method"
                            value={formData.preferred_contact_method}
                            onChange={handleChange}
                          >
                            {CONTACT_METHODS.map((m) => (
                              <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-0">
                          <Form.Label>Preferred Contact Time</Form.Label>
                          <Form.Control
                            type="text"
                            name="preferred_contact_time"
                            value={formData.preferred_contact_time}
                            onChange={handleChange}
                            placeholder="e.g. Weekday mornings, after 5pm"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* Section 6: Certification and Electronic Signature */}
                <Card className="roe-form-card roe-signature-card mb-4">
                  <Card.Body>
                    <h5 className="roe-section-title">
                      <span className="roe-section-number">6</span>
                      Certification &amp; Electronic Signature
                    </h5>

                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        id="certify_authorized"
                        name="certify_authorized"
                        checked={formData.certify_authorized}
                        onChange={handleChange}
                        isInvalid={!!errors.certify_authorized}
                        label="I certify that I am the property owner or an authorized representative with the legal authority to grant right of entry to the property described above."
                        feedback={errors.certify_authorized}
                        feedbackType="invalid"
                      />
                    </Form.Group>

                    <div className="roe-signature-area">
                      <Row>
                        <Col md={8}>
                          <Form.Group className="mb-3">
                            <Form.Label>Electronic Signature (type your full legal name) <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                              type="text"
                              name="electronic_signature"
                              value={formData.electronic_signature}
                              onChange={handleChange}
                              isInvalid={!!errors.electronic_signature}
                              placeholder="Full legal name"
                              className="roe-signature-input"
                            />
                            <Form.Control.Feedback type="invalid">{errors.electronic_signature}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>Date <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                              type="date"
                              name="signature_date"
                              value={formData.signature_date}
                              onChange={handleChange}
                              isInvalid={!!errors.signature_date}
                            />
                            <Form.Control.Feedback type="invalid">{errors.signature_date}</Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                      <p className="roe-signature-notice mb-0">
                        By typing your name above, you acknowledge that this electronic signature has the same legal effect as a handwritten signature pursuant to the Electronic Signatures in Global and National Commerce Act (E-SIGN Act).
                      </p>
                    </div>
                  </Card.Body>
                </Card>

                <div className="d-grid d-md-block mb-4">
                  <Button
                    variant="primary"
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    className="px-5"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Right of Entry Form'}
                  </Button>
                </div>

              </Form>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
}

export default RightOfEntry;
